<div class="dashboard-container">
  <div class="sticky-header">
    <h1 class="dashboard-title">LLM Intelligence</h1>
    <app-date-range-selector
      [selectedPeriod]="selectedPeriod()"
      [startDate]="startDate()"
      [endDate]="endDate()"
      [includeWeek]="true"
      (periodChange)="onPeriodChange($event)"
      (dateRangeChange)="onDateRangeChange($event)"
    />
  </div>

  <div class="tabs-container">
    <p-tabs [(value)]="activeTab" (valueChange)="onTabChange()">
      <p-tablist>
        <p-tab value="analysis">Internal Token Analysis</p-tab>
        <p-tab value="logs">Cost Audit Log</p-tab>
        <p-tab value="costs">Total Provider Costs</p-tab>
      </p-tablist>
    </p-tabs>
  </div>

  @if (llmService.errorMessage(); as error) {
    <app-error-banner [message]="error" />
  }

  <div class="dashboard-grid">
    @if (activeTab() === 'costs') {
      <div class="card-wrapper full-width">
        <div class="stats-grid mb-4">
          <div class="stat-card highlight">
            <div class="stat-title">Total Provider Cost</div>
            @if (loadingCosts()) {
              <div class="flex flex-column gap-2 mt-2">
                <p-skeleton width="10rem" height="2.5rem"></p-skeleton>
                <p-skeleton width="6rem" height="1rem"></p-skeleton>
              </div>
            } @else {
              <div class="stat-value text-green-600">
                {{ totalProviderCost() | currency: providerCurrency() : 'symbol' : '1.2-2' }}
              </div>
              <div class="stat-sub">Actual API Billing</div>
            }
          </div>
        </div>
        <p-card header="Daily Cost Trend">
          @if (!loadingCosts()) {
            <div class="chart-container" style="height: 15rem">
              <p-chart
                type="line"
                [data]="providerCostChartData()"
                [options]="providerCostChartOptions()"
                height="100%"
              ></p-chart>
            </div>
          } @else {
            <div class="w-full" style="height: 30rem">
              <p-skeleton width="100%" height="100%" borderRadius="12px"></p-skeleton>
            </div>
          }
        </p-card>
      </div>
    }

    @if (activeTab() === 'analysis') {
      <div class="card-wrapper full-width">
        @if (llmService.aggregatedStats(); as stats) {
          <div class="stats-grid mb-4">
            <div class="stat-card">
              <div class="stat-title">Internal Est. Cost</div>
              <div class="stat-value primary-text">
                {{ stats.totalCost | currency: 'EUR' : 'symbol' : '1.2-2' }}
              </div>
              <div class="stat-sub">Calculated from Usage</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">Total Tokens</div>
              <div class="stat-value">{{ stats.totalTokens | number }}</div>
              <div class="stat-sub">Volume</div>
            </div>
            <div class="stat-card">
              <div class="stat-title">Input / Cached</div>
              <div class="stat-value text-base">
                <div>In: {{ stats.inputTokens | number }}</div>
                <div class="text-green-600 text-sm">
                  Cached: {{ stats.cachedInputTokens | number }}
                </div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-title">Output / Reasoning</div>
              <div class="stat-value text-base">
                <div>Out: {{ stats.outputTokens | number }}</div>
                <div class="text-blue-600 text-sm">
                  Reasoning: {{ stats.reasoningTokens | number }}
                </div>
              </div>
            </div>
          </div>
        }
        <div class="analysis-grid">
          <div class="chart-card-wrapper">
            <p-card header="Token Usage by Prompt (Avg In/Out)">
              <p-chart
                type="bar"
                [data]="tokenChartData()"
                [options]="tokenChartOptions()"
                height="400px"
              ></p-chart>
            </p-card>
          </div>
          <div class="cost-card-wrapper">
            <p-card header="Top Cost Drivers">
              <p-table
                [value]="llmService.tokenAnalysis()"
                [rows]="10"
                [paginator]="true"
                styleClass="p-datatable-sm"
              >
                <ng-template pTemplate="header">
                  <tr>
                    <th>Prompt</th>
                    <th class="text-right">Calls</th>
                    <th class="text-right">Total Cost</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-item>
                  <tr>
                    <td>{{ item.promptName }}</td>
                    <td class="text-right">{{ item.totalCalls }}</td>
                    <td class="text-right font-bold">{{ item.totalCost | currency: 'EUR' }}</td>
                  </tr>
                </ng-template>
              </p-table>
            </p-card>
          </div>
        </div>
      </div>
    }

    @if (activeTab() === 'logs') {
      <div class="card-wrapper full-width">
        <p-card>
          <p-table
            [value]="llmService.llmLogs().data"
            [paginator]="true"
            [rows]="20"
            [lazy]="true"
            [lazyLoadOnInit]="false"
            (onLazyLoad)="loadLogs($event)"
            [totalRecords]="llmService.llmLogs().total"
            [loading]="loading()"
            styleClass="p-datatable-striped"
            [tableStyle]="{ 'min-width': '70rem' }"
          >
            <ng-template pTemplate="caption">
              <div class="flex gap-2 align-items-center">
                <span class="p-input-icon-left">
                  <i class="pi pi-search"></i>
                  <input
                    pInputText
                    type="text"
                    [ngModel]="filters().search"
                    (ngModelChange)="filters.update((f) => ({ ...f, search: $event }))"
                    (input)="onFilterChange()"
                    placeholder="Search user, org, prompt..."
                  />
                </span>
                <p-select
                  [options]="providerOptions"
                  [ngModel]="filters().provider"
                  (ngModelChange)="filters.update((f) => ({ ...f, provider: $event }))"
                  (onChange)="onFilterChange()"
                  placeholder="Provider"
                  [showClear]="true"
                  styleClass="w-10rem"
                ></p-select>
              </div>
            </ng-template>
            <ng-template pTemplate="header">
              <tr>
                <th>Date</th>
                <th>User</th>
                <th>Prompt Name</th>
                <th>Model</th>
                <th>Provider</th>
                <th>Tokens (In/Out)</th>
                <th>Cost</th>
                <th>Status</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-log>
              <tr class="clickable-row">
                <td>{{ log.createdAt | date: 'short' }}</td>
                <td>
                  @if (log.user) {
                    <div>
                      <a
                        class="link-cell"
                        tabindex="0"
                        role="link"
                        (click)="openUserSessions(log.user.id)"
                        (keydown.enter)="openUserSessions(log.user.id)"
                      >
                        {{ log.user.name }}
                      </a>
                    </div>
                  }
                  @if (log.organisationId !== null) {
                    <a
                      class="link-cell"
                      tabindex="0"
                      role="link"
                      (click)="openOrg(log.organisationId)"
                      (keydown.enter)="openOrg(log.organisationId)"
                    >
                      {{ log.user?.organisation || 'Organization' }}
                    </a>
                  } @else if (log.user) {
                    <div class="text-xs text-500">{{ log.user.organisation }}</div>
                  }
                </td>
                <td>
                  <p-tag [value]="log.promptName || 'Unknown'" severity="info"></p-tag>
                </td>
                <td>{{ log.model }}</td>
                <td>{{ log.provider }}</td>
                <td>
                  <div>{{ log.tokens.input }} / {{ log.tokens.output }}</div>
                  <div class="text-xs text-500">Total: {{ log.tokens.total }}</div>
                </td>
                <td class="font-bold">{{ log.cost | currency: 'EUR' : 'symbol' : '1.4-4' }}</td>
                <td>
                  <p-tag
                    [value]="log.status"
                    [severity]="log.status === 'Error' ? 'danger' : 'success'"
                  ></p-tag>
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="8" class="text-center p-4">No records found.</td>
              </tr>
            </ng-template>
          </p-table>
        </p-card>
      </div>
    }
  </div>
</div>
